import { isIOS } from '@vindr/utils/mobile';
import isEmpty from 'lodash/isEmpty';
import uniqBy from 'lodash/uniqBy';
import fixWebmDuration from 'webm-duration-fix';
import { DefaultOutputDevice, KEY_DEFAULT_CAMERA, VIDEO_SIZES } from '../context/state';
import { FacingMode, Specification, VIDEO_SIZES_TYPE } from '../context/type';
import { localStorageAction } from '@vindr/utils/helpers/storage';
import uniq from 'lodash/uniq';
import { isFirefox } from '@vindr/utils/helpers/detect';

export const getRecordingBlob = async (blob: Blob) => {
  if (!blob) return null;
  try {
    return await fixWebmDuration(blob);
  } catch (err) {
    return blob;
  }
};

export const polyfillCamera = async () => {
  //? If the browser doesn't support mediaDevices API
  if (navigator.mediaDevices === undefined) {
    return false;
  }

  if (navigator.mediaDevices.getUserMedia === undefined) {
    navigator.mediaDevices.getUserMedia = (constraints) => {
      const getUserMedia =
        navigator.webkitGetUserMedia || navigator.mozGetUserMedia || navigator.msGetUserMedia;

      if (!getUserMedia) {
        return Promise.reject(new Error('getUserMedia is not implemented in this browser'));
      }

      return new Promise((resolve, reject) => {
        getUserMedia.call(navigator, constraints, resolve, reject);
      });
    };
  }
};

const handleErrorGetCamera = (error: Error) => {
  switch (error.name) {
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      console.error('There is no connected camera.');
      break;

    case 'NotReadableError':
    case 'TrackStartError':
      console.error('Webcam or microphone are already in use');
      //? Resolve with null to allow the user to switch to another active device
      return error.name;

    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      console.error('Constraints cannot be satisfied by available devices');
      break;

    case 'NotAllowedError':
    case 'PermissionDeniedError':
      console.error('Permission denied in the browser');
      break;

    case 'TypeError':
      console.error('Empty constraints object');
      break;

    default:
      console.error('Other error:', error);
  }

  throw error;
};

export const requestAccess = async () => {
  try {
    let stream: MediaStream;

    //? Firefox allows users to always gate camera access behind a prompt but shows all device info via enumerateDevices first
    if (isFirefox()) {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const device = await getDefaultCamera(getCameraSpecification(devices, { position: 'back' }));

      stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        //? Specifying the deviceId is necessary to gain camera permissions in Firefox.
        video: { deviceId: device?.deviceId },
      });
    } else {
      //? In Chrome, enumerateDevices can be hidden but users can grant access to all cameras directly
      stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: true,
      });
    }

    //? Stop all tracks in the stream to clean up resources
    stream?.getTracks?.().forEach((track) => track?.stop?.());

    return stream;
  } catch (error) {
    return handleErrorGetCamera(error);
  }
};

export const sortingDevices = (
  devices: MediaDeviceInfo[]
): [InputDeviceInfo[], InputDeviceInfo[]] => {
  if (devices.length === 0) return;

  const [cameraDevices, micDevices] = devices.reduce(
    (acc, device: InputDeviceInfo) => {
      const [videoDevice, audioDevice] = [
        getDeviceKind(device, 'videoinput'),
        getDeviceKind(device, 'audioinput'),
      ];

      if (videoDevice) {
        acc[0].push(device);
      } else if (audioDevice) {
        acc[1].push(device);
      }

      return acc;
    },
    [[] as InputDeviceInfo[], [] as InputDeviceInfo[]]
  );

  return [uniqBy(cameraDevices, (x) => x?.deviceId), uniqBy(micDevices, (x) => x?.deviceId)];
};

export const getListResolution = async (selectedVideo: InputDeviceInfo) => {
  const result = [];

  const getVideoSettings = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: { exact: selectedVideo.deviceId },
          groupId: selectedVideo.groupId,
          width: { ideal: 4096 },
          height: { ideal: 2160 },
        },
      });

      if (!stream) return;

      const videoTrack = stream.getTracks()[0];
      const settings = videoTrack?.getSettings();

      videoTrack?.stop();
      stream?.getTracks?.().forEach((track) => track.stop());
      return settings;
    } catch (error) {
      return null;
    }
  };

  const processResolution = (width: number, height: number, aspectRatio: number) => {
    const SIZES =
      width > height
        ? VIDEO_SIZES
        : VIDEO_SIZES.map((size) => ({
            ...size,
            width: size.height,
            height: size.width,
          }));
    SIZES.forEach((size) => {
      if (height >= size.height && aspectRatio === size.width / size.height) {
        result.push(size);
      }
    });
  };

  // const deviceInfo = selectedVideo.getCapabilities?.();
  const deviceInfo = null;
  //TODO: Don't know what orientation of camera
  if (isEmpty(deviceInfo)) {
    const settings = await getVideoSettings();
    if (settings) {
      const { width: widthCameraSource, height: heightCameraSource, aspectRatio } = settings;
      processResolution(widthCameraSource, heightCameraSource, aspectRatio);
    }
  } else {
    const { width: widthCameraSource, height: heightCameraSource, aspectRatio } = deviceInfo;
    processResolution(widthCameraSource.max, heightCameraSource.max, aspectRatio.max);
  }

  return result as typeof VIDEO_SIZES;
};

/* export const getListResolution = async (selectedVideo: InputDeviceInfo) => {
  const result = [];

  await navigator.mediaDevices
    .getUserMedia({
      video: {
        deviceId: { exact: selectedVideo.deviceId },
        groupId: selectedVideo.groupId,
        width: { ideal: 4096 },
        height: { ideal: 2160 },
      },
    })
    .then((stream) => {
      if (!stream) return;

      stream.getTracks().forEach((track) => {
        if (track.kind === 'video') {
          const widthCameraSource = track.getSettings().width;
          const heightCameraSource = track.getSettings().height;
          const aspectRatio = track.getSettings().aspectRatio;

          const SIZES =
            widthCameraSource > heightCameraSource
              ? VIDEO_SIZES
              : VIDEO_SIZES.map((size) => ({
                  ...size,
                  width: size.height,
                  height: size.width,
                }));

          SIZES.forEach((size) => {
            if (heightCameraSource >= size.height && aspectRatio === size.width / size.height) {
              result.push(size);
            }
          });
        }
      });

      stream.getTracks().forEach((track) => track.stop());
    })
    .catch(() => null);

  return result as typeof VIDEO_SIZES;
}; */

export function appendImgPreview(url: string) {
  const img = document.createElement('img');
  img.classList.add('img-preview');
  img.src = url;
  img.alt = 'Preview image';

  const previewStack = document.querySelector('.preview-image-stack');
  if (!previewStack) return;

  previewStack.prepend(img);
  setTimeout(() => {
    previewStack.removeChild(img);
  }, 3000);
}

//? https://developer.apple.com/forums/thread/690523
//? Make video scrolling on iOS by adding Shadow Content to the video tag. Optionally enable controls; remove controls attribute if not needed.
export function fixVideoCantScroll(element: HTMLVideoElement, enableControl = false) {
  if (!isIOS()) return;

  element.setAttribute('controls', 'true');

  if (enableControl) return;

  element.removeAttribute('controls');
}

export function getDeviceKind(device: MediaDeviceInfo, kind: MediaDeviceKind) {
  return device?.deviceId && device?.kind === kind ? device : false;
}

export function getCameraSpecification(devices: MediaDeviceInfo[], specification: Specification) {
  const videoDevices = devices.filter((device) =>
    getDeviceKind(device, 'videoinput')
  ) as InputDeviceInfo[];

  let result = [...videoDevices];

  Object.entries(specification).forEach(
    ([key, val]: [keyof Specification, Specification[keyof Specification]]) => {
      switch (key) {
        case 'position': {
          const videoPosition = [];
          const mapPositionCamera: Record<Specification['position'], FacingMode> = {
            front: 'user',
            back: 'environment',
            left: 'left',
            right: 'right',
          };

          //? Check by facingMode
          for (const device of videoDevices) {
            if (!('getCapabilities' in device)) continue;
            const deviceInfo = (device as MediaStreamTrack | any)?.getCapabilities?.();

            if (deviceInfo?.facingMode?.includes(mapPositionCamera[val])) {
              videoPosition.push(device);
            }
          }

          //? When no device has facingMode or Firefox doesn't support getCapabilities
          if (videoPosition.length === 0) {
            getSpecificationByCameraLabel(videoDevices, val).forEach((val) =>
              videoPosition.push(val)
            );
          }

          if (videoPosition.length > 0) result = videoPosition;
          break;
        }
        default:
          break;
      }
    }
  );

  return result;
}

function getSpecificationByCameraLabel(
  videoDevices: InputDeviceInfo[],
  val: Specification[keyof Specification]
) {
  const result = [];

  for (const device of videoDevices) {
    const deviceName = device.label;
    const isBackCamera = isBackCameraLabel(deviceName);

    const devices: Record<Specification['position'], boolean | InputDeviceInfo> = {
      'front': !isBackCamera && device,
      'back': isBackCamera && device,
      'left': device,
      'right': device,
    };

    const deviceValid = devices[val];

    if (!isEmpty(deviceValid)) result.push(deviceValid);
  }

  return result;
}

function isBackCameraLabel(label: string): boolean {
  const frontCameraKey = ['front', 'trước', 'frontal'];

  return !frontCameraKey.every((key) => new RegExp(key, 'i').test(label));
}

export async function getDefaultCamera(devices: InputDeviceInfo[]) {
  if (!devices?.length) return;

  const cacheOutputDevice = localStorageAction.get(KEY_DEFAULT_CAMERA)?.label || [];
  const cacheAndDefaultOutput = uniq([...cacheOutputDevice, ...DefaultOutputDevice]);

  const devicePrefer = devices.sort((device) =>
    cacheAndDefaultOutput.includes(device.label) ? -1 : 1
  );

  for (const device of devicePrefer) {
    try {
      const stream = (await getCamera(device, false)) as MediaStream;

      stream?.getTracks?.().forEach((track) => track?.stop?.());

      return device;
    } catch (error) {
      console.log(`📕 error - 339:webcam-helper.ts \n`, error);
      continue;
    }
  }

  return devicePrefer[0] || null;
}

export async function getCamera(
  selectedVideo: InputDeviceInfo,
  enableMic: boolean,
  selectedResolution?: VIDEO_SIZES_TYPE
) {
  try {
    return await navigator.mediaDevices.getUserMedia({
      video: {
        deviceId: selectedVideo?.deviceId,
        groupId: selectedVideo?.groupId,
        ...(selectedResolution && {
          width: { ideal: selectedResolution.width },
          height: { ideal: selectedResolution.height },
        }),
      },
      audio: enableMic,
    });
  } catch (error) {
    return handleErrorGetCamera(error);
  }
}

export const ffmpegOptionByExtension = {
  'webm': ['-c', 'copy', '-preset', 'ultrafast'],
  'default': [
    '-c:a',
    'copy',
    '-preset',
    'ultrafast',
    '-movflags',
    'faststart',
    '-profile:v',
    'high',
  ],
};

export function setSrcObject(videoElement: HTMLVideoElement, stream: MediaStream) {
  if (videoElement.srcObject !== undefined) {
    videoElement.srcObject = stream;
  } else if (window.videoEl.mozSrcObject !== undefined) {
    videoElement.mozSrcObject = stream;
  } else if (window.URL.createObjectURL) {
    videoElement.src = window.URL.createObjectURL(stream);
  } else if (window.webkitURL) {
    videoElement.src = window.webkitURL.createObjectURL(stream);
  } else {
    videoElement.src = stream;
  }
}

type Permission =
  | 'accelerometer'
  | 'accessibility-events'
  | 'ambient-light-sensor'
  | 'background-sync'
  | 'camera'
  | 'clipboard-read'
  | 'clipboard-write'
  | 'geolocation'
  | 'gyroscope'
  | 'local-fonts'
  | 'magnetometer'
  | 'microphone'
  | 'midi'
  | 'notifications'
  | 'payment-handler'
  | 'persistent-storage'
  | 'push'
  | 'screen-wake-lock'
  | 'storage-access'
  | 'top-level-storage-access'
  | 'window-management';

export async function checkPermission(permission: Permission) {
  const result = await navigator.permissions.query({ name: permission as any });
  return result.state === 'granted';
}

export async function checkPermissions(permissions: Permission[]) {
  const permissionPromises = permissions.map(checkPermission);
  const results = await Promise.all(permissionPromises);
  return results.every(Boolean);
}
